import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFileCsv,
    faSpinner,
    faTableList,
    faDownload,
    faClockRotateLeft,
    faCircleInfo,
    faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { useFlashMessage } from '../../components/FlashMessage';
import { extractApiError } from '../../utils/errorMessages';
import {
    fetchAccountingExport,
    previewAccountingExport,
    downloadAccountingExport,
    fetchAccountingExportRuns,
    downloadAccountingExportRun,
} from '../../services/reportingService';
import ResultTable from './ResultTable';
import { formatCell, formatDateTime } from './formatCell';
import { monthChoices, monthWords } from './exportMonths';

/**
 * Accounting export (ledger 200): a company's month as journal lines, shown
 * first, then taken as a CSV for the office's own accounting package.
 *
 * The server decides everything: which companies this person may export (the
 * finance right, their own companies, books not kept elsewhere), which
 * documents are new since the last export, what each line holds, and which
 * lines have no account. Taking the file records the export, so the same
 * documents never come out twice; "Download again" hands back exactly the
 * file a past export gave.
 */
const AccountingExport = () => {
    const { showError, showSuccess } = useFlashMessage();
    const [companies, setCompanies] = useState([]);
    const [currentMonth, setCurrentMonth] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [company, setCompany] = useState('');
    const [month, setMonth] = useState('');
    const [preview, setPreview] = useState(null);
    // The selection the preview on screen was made for: a changed company or
    // month makes the download wait for a fresh "Show lines".
    const [previewFor, setPreviewFor] = useState(null);
    const [running, setRunning] = useState(false);
    const [runError, setRunError] = useState('');
    const [downloading, setDownloading] = useState(false);

    const [runs, setRuns] = useState([]);
    const [runsLoading, setRunsLoading] = useState(false);
    const [runsError, setRunsError] = useState('');
    const [busyRunId, setBusyRunId] = useState(null);

    // `silent` re-reads the companies (for the "last export" line) without
    // blanking the panel, as after a download.
    const loadCompanies = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        setError('');
        try {
            const data = await fetchAccountingExport();
            setCompanies(data.companies || []);
            setCurrentMonth(data.current_month || '');
            // Open on a company that can export, when there is one; read-only ones follow.
            const first = (data.companies || []).find((c) => c.export_offered === true) || data.companies?.[0];
            setCompany((current) => current || String(first?.id || ''));
            setMonth((current) => current || data.current_month || '');
        } catch (err) {
            if (!silent) setError(extractApiError(err, 'Could not load the accounting export.'));
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCompanies();
    }, [loadCompanies]);

    // The company chosen right now: a past-exports reply for any other company is
    // dropped, so a slow reply never lists one company's files under another's name.
    const chosenRef = useRef(company);
    chosenRef.current = company;
    const loadRuns = useCallback(async (companyId) => {
        if (!companyId) {
            setRuns([]);
            return;
        }
        const stillChosen = () => String(chosenRef.current) === String(companyId);
        // A load for a company no longer chosen (a download finished after a switch)
        // must not touch the loading state either, or 'Loading…' would never clear.
        if (!stillChosen()) return;
        setRunsLoading(true);
        setRunsError('');
        try {
            const data = await fetchAccountingExportRuns(companyId);
            if (stillChosen()) setRuns(data.runs || []);
        } catch (err) {
            if (stillChosen()) setRunsError(extractApiError(err, 'Could not load the past exports for this company.'));
        } finally {
            if (stillChosen()) setRunsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadRuns(company);
    }, [company, loadRuns]);

    const months = useMemo(() => monthChoices(currentMonth), [currentMonth]);
    const chosenCompany = companies.find((c) => String(c.id) === String(company)) || null;
    // A company whose books moved to the accounting system keeps its past exports
    // readable but makes no new file; the server says so and why.
    // Only a company the server marks as exporting gets the buttons; anything else is read-only.
    const readOnly = chosenCompany ? chosenCompany.export_offered !== true : false;

    const showLines = async () => {
        if (!company || !month) return;
        setRunning(true);
        setRunError('');
        try {
            const data = await previewAccountingExport({ company, month });
            setPreview(data);
            setPreviewFor({ company: String(company), month });
        } catch (err) {
            setPreview(null);
            setPreviewFor(null);
            setRunError(extractApiError(err, 'Could not work out the journal lines.'));
        } finally {
            setRunning(false);
        }
    };

    const previewIsCurrent = Boolean(
        preview && previewFor && previewFor.company === String(company) && previewFor.month === month,
    );
    const canDownload = previewIsCurrent && preview.documents > 0 && !running && !downloading;

    const takeFile = async () => {
        setDownloading(true);
        try {
            // The key says which lines were looked at; the server refuses the run if
            // anything changed in between, so the file is always what was shown.
            const fileName = await downloadAccountingExport({ company, month, preview_key: preview.preview_key });
            showSuccess(
                `Saved ${fileName}. ${formatCell(preview.documents, 'integer')} document`
                + `${preview.documents === 1 ? ' is' : 's are'} now marked as exported for `
                + `${chosenCompany?.name || 'this company'}.`,
                7000,
            );
            await Promise.all([loadRuns(company), loadCompanies({ silent: true })]);
            // The lines just taken are exported now: show what is left (nothing, usually).
            await showLines();
        } catch (err) {
            showError(extractApiError(err, 'Could not make the export.'), 7000);
            // The server refused (the lines changed, or nothing is left): read them again
            // so the screen shows the truth and the button matches it.
            await showLines();
        } finally {
            setDownloading(false);
        }
    };

    const downloadAgain = async (run) => {
        setBusyRunId(run.id);
        try {
            const fileName = await downloadAccountingExportRun(run);
            showSuccess(`Saved ${fileName}.`);
        } catch (err) {
            showError(extractApiError(err, 'Could not open that export again.'));
        } finally {
            setBusyRunId(null);
        }
    };

    if (loading) {
        return <div className="rc-panel rc-panel-message">Loading the accounting export…</div>;
    }
    if (error) {
        return (
            <div className="rc-panel">
                <div className="rc-error">
                    <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
                </div>
                <div className="rc-filter-actions">
                    <button type="button" className="rc-btn-ghost" onClick={() => loadCompanies()}>
                        Try again
                    </button>
                </div>
            </div>
        );
    }
    if (companies.length === 0) {
        return (
            <div className="rc-panel">
                <div className="rc-empty">
                    <FontAwesomeIcon icon={faFileCsv} className="rc-empty-icon" />
                    <div className="rc-empty-title">No company to export</div>
                    <p>
                        None of your companies exports its books to a file. A company whose books
                        are kept in the accounting system directly has its record there.
                    </p>
                </div>
            </div>
        );
    }

    // Lines are shown only for the company and month they were worked out for.
    const tableResult = preview && previewIsCurrent ? { ...preview, totals: null } : null;

    return (
        <div className="rc-export">
            <div className="rc-panel">
                <p className="rc-export-intro">
                    The month&apos;s customer invoices, credit notes, supplier invoices and debit notes as
                    journal lines, balanced per document, in one standard CSV for your accounting package.
                    A document is exported once; anything issued after an export comes out in the next one.
                </p>
                <div className="rc-filter-bar">
                    <div className="rc-field">
                        <label className="rc-field-label" htmlFor="rc-export-company">Company</label>
                        <select
                            id="rc-export-company"
                            className="rc-select"
                            value={company}
                            onChange={(e) => setCompany(e.target.value)}
                        >
                            {companies.map((c) => (
                                <option key={c.id} value={String(c.id)}>
                                    {c.export_offered === true ? c.name : `${c.name} (past exports only)`}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="rc-field">
                        <label className="rc-field-label" htmlFor="rc-export-month">Month</label>
                        <select
                            id="rc-export-month"
                            className="rc-select"
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                        >
                            {months.map((m) => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </select>
                    </div>
                    {!readOnly && (
                        <div className="rc-filter-actions">
                            <button
                                type="button"
                                className="rc-btn-primary"
                                onClick={showLines}
                                disabled={running || downloading || !company || !month}
                            >
                                <FontAwesomeIcon icon={running ? faSpinner : faTableList} spin={running} />
                                {running ? 'Working…' : 'Show lines'}
                            </button>
                            <button
                                type="button"
                                className="rc-btn-ghost"
                                onClick={takeFile}
                                disabled={!canDownload}
                                title={previewIsCurrent ? undefined : 'Show the lines first'}
                            >
                                <FontAwesomeIcon icon={downloading ? faSpinner : faFileCsv} spin={downloading} />
                                {downloading ? 'Exporting…' : 'Download CSV'}
                            </button>
                        </div>
                    )}
                </div>
                {readOnly ? (
                    <div className="rc-note">
                        <FontAwesomeIcon icon={faCircleInfo} /> {chosenCompany.read_only_reason} Its past exports stay below.
                    </div>
                ) : null}
                {chosenCompany?.last_export ? (
                    <div className="rc-export-last">
                        <FontAwesomeIcon icon={faClockRotateLeft} />
                        {' '}Last export for {chosenCompany.name}: {chosenCompany.last_export.month_words},
                        {' '}taken {formatDateTime(chosenCompany.last_export.created_at)}
                        {chosenCompany.last_export.run_by ? ` by ${chosenCompany.last_export.run_by}` : ''}.
                    </div>
                ) : (
                    <div className="rc-export-last">
                        <FontAwesomeIcon icon={faCircleInfo} /> No export yet for {chosenCompany?.name || 'this company'}.
                    </div>
                )}
            </div>

            {preview && previewIsCurrent && !running && !runError && (
                <div className="rc-panel">
                    <div className="rc-export-summary">
                        <div className="rc-export-stat">
                            <span className="rc-export-stat-label">Documents</span>
                            <strong>{formatCell(preview.documents, 'integer')}</strong>
                        </div>
                        <div className="rc-export-stat">
                            <span className="rc-export-stat-label">Journal lines</span>
                            <strong>{formatCell(preview.lines, 'integer')}</strong>
                        </div>
                        {(preview.totals_by_currency || []).map((t) => (
                            <div className="rc-export-stat" key={t.currency || 'none'}>
                                <span className="rc-export-stat-label">Debit = credit ({t.currency || 'no currency'})</span>
                                <strong>
                                    {formatCell(t.debit, 'money')} = {formatCell(t.credit, 'money')}
                                </strong>
                            </div>
                        ))}
                        <div className="rc-export-stat">
                            <span className="rc-export-stat-label">Balanced per document</span>
                            <strong>{preview.balanced ? 'Yes' : 'No'}</strong>
                        </div>
                        <div className="rc-export-stat">
                            <span className="rc-export-stat-label">Lines with no account</span>
                            <strong>{formatCell(preview.unmapped_lines, 'integer')}</strong>
                        </div>
                    </div>
                </div>
            )}

            {!readOnly && (
            <div className="rc-panel">
                <ResultTable
                    result={tableResult}
                    running={running}
                    error={runError}
                    caption={`Journal lines for ${chosenCompany?.name || ''}, ${monthWords(month)}`}
                    idleText="Choose the company and the month, then press Show lines."
                    runningText="Working out the journal lines…"
                    emptyText={`Nothing new to export for ${chosenCompany?.name || 'this company'} up to ${monthWords(month)}: every document in the books up to then is already in a past export.`}
                />
            </div>
            )}

            <div className="rc-panel">
                <div className="rc-section-title">
                    <FontAwesomeIcon icon={faClockRotateLeft} /> Past exports for {chosenCompany?.name || 'this company'}
                </div>
                {runsLoading ? (
                    <div className="rc-drawer-note">Loading past exports…</div>
                ) : runsError ? (
                    <div className="rc-error">
                        <FontAwesomeIcon icon={faTriangleExclamation} /> {runsError}
                    </div>
                ) : runs.length === 0 ? (
                    <div className="rc-drawer-note">
                        No export yet. Every export is listed here with who took it and when, so the
                        same file can be downloaded again.
                    </div>
                ) : (
                    <ul className="rc-run-list">
                        {runs.map((run) => (
                            <li key={run.id} className="rc-run-row">
                                <div className="rc-run-main">
                                    <div className="rc-run-name">{run.month_words} · {run.file_name}</div>
                                    <div className="rc-run-meta">
                                        Exported {formatDateTime(run.created_at)}
                                        {run.run_by ? ` by ${run.run_by}` : ''}
                                        {' · '}
                                        {formatCell(run.documents, 'integer')} document{run.documents === 1 ? '' : 's'}
                                        {' · '}
                                        {formatCell(run.lines, 'integer')} line{run.lines === 1 ? '' : 's'}
                                        {run.unmapped_lines
                                            ? ` · ${formatCell(run.unmapped_lines, 'integer')} with no account`
                                            : ''}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="rc-btn-ghost"
                                    onClick={() => downloadAgain(run)}
                                    disabled={busyRunId === run.id}
                                >
                                    <FontAwesomeIcon
                                        icon={busyRunId === run.id ? faSpinner : faDownload}
                                        spin={busyRunId === run.id}
                                    />
                                    {busyRunId === run.id ? 'Opening…' : 'Download again'}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default AccountingExport;
